/**
 * Task-pane card for one paused native question.
 *
 * The user picks a listed option or types an answer. Either submission resumes
 * the same run. This is not an Excel dialog.
 */

import * as React from "react";
import type { UserQuestion, UserQuestionAnswer } from "../userQuestion";

/**
 * Render one question, its options, and a typed answer.
 *
 * @param question - Validated question emitted by the native run.
 * @param onAnswer - Called once with the selected option or typed text.
 * @returns The question card.
 */
export const ExcelUserQuestionCard: React.FC<{
  question: UserQuestion;
  onAnswer: (answer: UserQuestionAnswer) => void;
}> = ({ question, onAnswer }) => {
  const questionId = React.useId();
  const [typed, setTyped] = React.useState("");
  const [submitted, setSubmitted] = React.useState(false);

  const submit = (answer: UserQuestionAnswer) => {
    if (submitted) return;
    setSubmitted(true);
    onAnswer(answer);
  };

  return (
    <section
      aria-labelledby={questionId}
      className="mx-3 mb-2 rounded-md border border-border bg-background px-3 py-2.5"
      data-user-question-card="true"
    >
      <p id={questionId} className="text-[13px] leading-relaxed text-foreground">
        {question.question}
      </p>
      <div className="mt-2 flex flex-col gap-1.5">
        {question.options.map((option) => {
          const recommended = option.id === question.recommendedOptionId;
          return (
            <button
              key={option.id}
              type="button"
              disabled={submitted}
              onClick={() => submit({ answer: option.label, optionId: option.id })}
              className="rounded-md border border-border bg-background px-2.5 py-1.5 text-left text-[12.5px] leading-snug text-foreground disabled:opacity-60"
            >
              <span>{option.label}</span>
              {recommended && (
                <span className="ml-2 text-[11px] text-muted-foreground">Recommended</span>
              )}
            </button>
          );
        })}
      </div>
      <form
        className="mt-2 flex items-center gap-1.5"
        onSubmit={(event) => {
          event.preventDefault();
          const text = typed.trim();
          if (!text) return;
          submit({ answer: text, optionId: null });
        }}
      >
        <input
          value={typed}
          disabled={submitted}
          onChange={(event) => setTyped(event.target.value)}
          maxLength={500}
          placeholder="Or type an answer"
          aria-label="Typed answer"
          className="min-w-0 flex-1 rounded-md border border-border bg-background px-2 py-1.5 text-[12.5px] text-foreground"
        />
        <button
          type="submit"
          disabled={submitted || typed.trim().length === 0}
          className="rounded-md border border-border px-2.5 py-1.5 text-[12.5px] text-foreground disabled:opacity-60"
        >
          Send
        </button>
      </form>
    </section>
  );
};
